import * as React from 'react';
import { observer } from 'mobx-react';
import { Route, Switch, Redirect, withRouter, RouteComponentProps } from 'react-router-dom';

import { AzureAuth } from '@app/Components/AzureAuth';
import ErrorBoundary from '@app/Components/ErrorBoundary';
import { Layout } from '@app/Components/Layout';
import { appStore } from '@app/Stores/AppStore';

import JobList from '@app/Pages/Job/JobList';
import { NotFound } from '@app/Pages/NotFound/NotFound';
import { Loading } from './Components/Loading';
import { EdwRoute } from './EdwRoute';
import { StationsModuleProvider } from "./Context/StationsModuleProvider";
import { AircraftsModuleProvider } from "./Context/AircraftsModuleProvider";

const HealthBoard = React.lazy(() => import('@app/Pages/HealthBoard/HealthBoard'));
const FlightSchedule = React.lazy(() => import('@app/Pages/FlightSchedule/FlightSchedule'));
const EmailViewer = React.lazy(() => import('@app/Pages/EmailViewer/EmailViewer'));
const Configs = React.lazy(() => import('@app/Pages/Configs/Configs'));
const Stations = React.lazy(() => import('@app/Pages/Stations/Stations'));
const StationDetailsPage = React.lazy(() => import('@app/Pages/StationDetails/StationDetailsPage'));
const Aircrafts = React.lazy(() => import('@app/Pages/Aircrafts/Aircrafts'));
const AircraftDetailsPage = React.lazy(() => import('@app/Pages/AircraftDetails/AircraftDetailsPage'));
const PeopleAssistViewer = React.lazy(() => import('@app/Pages/PeopleAssistViewer/PeopleAssistViewer'));
const PeopleAssistLiveViewer = React.lazy(() => import('@app/Pages/PeopleAssistLiveViewer/PeopleAssistLiveViewer'));
const PeopleAssistIllness = React.lazy(() => import('@app/Pages/PeopleAssistIllness/PeopleAssistIllness'));
const FileExplorer = React.lazy(() => import('@app/Pages/FileExplorer/FileExplorer'));
const PermissionsTool = React.lazy(() => import('@app/Pages/PermissionsTool/PermissionsTool'));
const KafkaViewer = React.lazy(() => import('@app/Pages/KafkaViewer/KafkaViewer'));

const RouteErrorBoundary = withRouter(({ location, children }: RouteComponentProps & React.PropsWithChildren) => (
    <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
));

const AppRoutes = observer(() => {
    // Wait for the feature flag before gating, otherwise a stale default could redirect to /403.
    if (!AzureAuth.permissionsFlagLoaded || (AzureAuth.permissionsEnabled && !AzureAuth.permissionsLoaded)) {
        return <Loading loading />;
    }

    const isDcc = AzureAuth.isDcc;
    const isImpersonating = !!appStore.permissionImpersonation;

    return (
    <Layout>
        <RouteErrorBoundary>
            <React.Suspense fallback={<Loading loading />}>
                <Switch>
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/" exact path="/" component={JobList} />}
                    {!(isDcc && AzureAuth.canSeeGeneralAdmin) && <Redirect exact from="/" to={AzureAuth.defaultRoute} />}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/health-board" exact path="/health-board" component={HealthBoard} />}
                    {AzureAuth.canSeeFlights && <EdwRoute key="/flight-schedule" path={'/flight-schedule'} component={FlightSchedule} />}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/email-viewer" exact path="/email-viewer" component={EmailViewer} />}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/configs" exact path="/configs" component={Configs} />}
                    {AzureAuth.canSeeStations && (
                        <EdwRoute
                            key="/stations"
                            exact
                            path="/stations"
                            render={(props) => (
                                <StationsModuleProvider>
                                    <Stations {...props} />
                                </StationsModuleProvider>
                            )}
                        />
                    )}
                    {AzureAuth.canSeeStations && (
                        <EdwRoute
                            key="/stations/:fourLetterCode"
                            path="/stations/:fourLetterCode"
                            render={(props) => (
                                <StationsModuleProvider>
                                    <StationDetailsPage {...props} />
                                </StationsModuleProvider>
                            )}
                        />
                    )}
                    {AzureAuth.canSeeAircrafts && (
                        <EdwRoute
                            key="/aircrafts"
                            exact
                            path="/aircrafts"
                            render={(props) => (
                                <AircraftsModuleProvider>
                                    <Aircrafts {...props} />
                                </AircraftsModuleProvider>
                            )}
                        />
                    )}
                    {AzureAuth.canSeeAircrafts && (
                        <EdwRoute
                            key="/aircrafts/:registrationNumber"
                            path="/aircrafts/:registrationNumber"
                            render={(props) => (
                                <AircraftsModuleProvider>
                                    <AircraftDetailsPage {...props} />
                                </AircraftsModuleProvider>
                            )}
                        />
                    )}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/people-assist/history" exact path="/people-assist/history" component={PeopleAssistViewer} />}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/people-assist/viewer" exact path="/people-assist/viewer" component={PeopleAssistLiveViewer} />}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/people-assist/illness" exact path="/people-assist/illness/:personCode?" component={PeopleAssistIllness} />}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <Redirect exact from="/people-assist" to="/people-assist/history" />}
                    {isDcc && AzureAuth.canSeeAdminTools && <EdwRoute key="/file-explorer" exact path="/file-explorer" component={FileExplorer} />}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/permissions-tool" exact path="/permissions-tool" component={PermissionsTool} />}
                    {isDcc && AzureAuth.canSeeGeneralAdmin && <EdwRoute key="/kafka-viewer" exact path="/kafka-viewer" component={KafkaViewer} />}
                    {isImpersonating && AzureAuth.defaultRoute !== '/403'
                        ? <Redirect to={AzureAuth.defaultRoute} />
                        : <EdwRoute key="*" path="*" component={NotFound} />}
                </Switch>
            </React.Suspense>
        </RouteErrorBoundary>
    </Layout>
    );
});

export const routes = <AppRoutes />;